import axios from 'axios';
import { Pet, PetSpecies, PetGender, Vaccination } from '@/types/telemedicine';
import { User, UserRole, UserStatus } from '@/types/users';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:3001/api/v1';
const TOKEN_KEY = 'pulsepet_token';

// Cliente base
const api = axios.create({
    baseURL: API_BASE_URL,
    withCredentials: true,
    headers: {
        'Content-Type': 'application/json',
    },
});

// Interceptor de request: não precisamos mais injetar token do localStorage, HttpOnly cookies fazem isso.
api.interceptors.request.use((config) => {
    return config;
});

// Interceptor de response: trata 401 (token expirado)
api.interceptors.response.use(
    (response) => response,
    async (error) => {
        const originalRequest = error.config;

        if (error.response?.status === 401 && !originalRequest._retry && !originalRequest.url?.includes('/auth/login') && !originalRequest.url?.includes('/auth/refresh')) {
            originalRequest._retry = true;
            
            try {
                await axios.post(`${API_BASE_URL}/auth/refresh`, {}, { withCredentials: true });
                // Refresh bem sucedido, o novo cookie HttpOnly foi recebido
                // Re-executar a request original
                return api(originalRequest);
            } catch (refreshError) {
                // Refresh falhou, limpar e redirecionar
                localStorage.removeItem(TOKEN_KEY);
                if (window.location.pathname.startsWith('/telemedicina') && !window.location.pathname.includes('/auth')) {
                    window.location.href = '/telemedicina/auth';
                }
                return Promise.reject(refreshError);
            }
        }
        
        // Erros que não são 401 ou rotas onde 401 é esperado (login/refresh)
        if (error.response?.status === 401 && (originalRequest.url?.includes('/auth/login') || originalRequest.url?.includes('/auth/refresh'))) {
             localStorage.removeItem(TOKEN_KEY);
             if (window.location.pathname.startsWith('/telemedicina') && !window.location.pathname.includes('/auth')) {
                 window.location.href = '/telemedicina/auth';
             }
        }

        return Promise.reject(error);
    }
);

// Helpers para token legados (mocks para não quebrar UI existente)
export const saveToken = (token: string) => { /* Token is handled via HttpOnly cookie */ };
export const removeToken = () => { /* Handled via /auth/logout endpoint */ };
export const getToken = () => { return "http-only-cookie"; };

// =============================================================
// Auth
// =============================================================
export const authApi = {
    login: (email: string, password: string) =>
        api.post('/auth/login', { email, password }).then((r) => r.data),

    register: (data: {
        name: string;
        email: string;
        password: string;
        phone?: string;
        cpf?: string;
        city?: string;
        state?: string;
        imei?: string;
    }) => api.post('/auth/register', data).then((r) => r.data),

    me: () => api.get('/auth/me').then((r) => r.data),

    logout: () => api.post('/auth/logout').then((r) => r.data),
};

// =============================================================
// Pets
// =============================================================
export const petsApi = {
    list: () => api.get('/pets').then((r) => r.data),

    get: (id: string) => api.get(`/pets/${id}`).then((r) => r.data),

    create: (data: {
        name: string;
        species: string;
        breed?: string;
        date_of_birth?: string;
        approximate_age?: number;
        gender: string;
        weight?: number;
        photo_url?: string;
        health_history?: string;
        allergies?: string[];
        vaccinations?: Partial<Vaccination>[];
        tracker_id?: string;
    }) => api.post<Pet>('/pets', data).then((r) => r.data),

    getTrackersAvailable: () => api.get<{ id: string; imei: string; status: string }[]>('/pets/trackers/available').then((r) => r.data),

    update: (id: string, data: Partial<Parameters<typeof petsApi.create>[0]>) =>
        api.patch(`/pets/${id}`, data).then((r) => r.data),

    remove: (id: string) => api.delete(`/pets/${id}`),
};

// =============================================================
// Veterinários
// =============================================================
export const veterinariansApi = {
    list: (params?: { specialty?: string; location?: string; available?: boolean }) =>
        api.get('/veterinarians', { params }).then((r) => r.data),

    get: (id: string) => api.get(`/veterinarians/${id}`).then((r) => r.data),

    update: (id: string, data: object) =>
        api.patch(`/veterinarians/${id}`, data).then((r) => r.data),

    getReviews: (id: string) => api.get(`/veterinarians/${id}/reviews`).then((r) => r.data),
};

// =============================================================
// Arquivos
// =============================================================
export const filesApi = {
    upload: async (file: File) => {
        const formData = new FormData();
        formData.append('file', file);
        return api.post('/upload', formData, {
            headers: {
                'Content-Type': 'multipart/form-data'
            }
        }).then(r => r.data);
    }
};

// =============================================================
// Perfil do Tutor (Users)
// =============================================================
export const profileApi = {
    updateProfile: (data: Record<string, unknown>) => api.patch('/users/me', data).then(r => r.data),
    updateAddress: (data: Record<string, unknown>) => api.post('/users/me/address', data).then(r => r.data),
    updateConsents: (consents: Array<Record<string, unknown>>) => api.post('/users/me/consents', { consents }).then(r => r.data),
    getNotificationSettings: () => api.get('/users/me/notifications/settings').then(r => r.data),
    updateNotificationSettings: (data: Record<string, unknown>) => api.patch('/users/me/notifications/settings', data).then(r => r.data)
};

// =============================================================
// Agendamentos
// =============================================================
export const appointmentsApi = {
    list: () => api.get('/appointments').then((r) => r.data),

    get: (id: string) => api.get(`/appointments/${id}`).then((r) => r.data),

    create: (data: {
        pet_id: string;
        veterinarian_id: string;
        type: string;
        scheduled_date: string;
        urgency_level?: string;
        symptoms?: object;
        notes?: string;
        access_link?: string;
        attachments?: string[];
    }) => api.post('/appointments', data).then((r) => r.data),

    updateStatus: (id: string, status: string) =>
        api.patch(`/appointments/${id}/status`, { status }).then((r) => r.data),

    cancel: (id: string) => api.delete(`/appointments/${id}`).then((r) => r.data),
};

// =============================================================
// Pagamentos
// =============================================================
export const paymentApi = {
    checkOrder: (email: string) => api.get(`/payments/check-order/${email}`).then((r) => r.data),
};

// Admin API
export const adminApi = {
    listUsers: () => api.get('/users').then(res => res.data),
    getUser: (id: string) => api.get<User>(`/users/${id}`).then(res => res.data),
    updateUserStatus: (id: string, status: UserStatus) => api.patch<User>(`/users/${id}/status`, { status }).then(res => res.data),
    updateUser: (id: string, data: Partial<User>) => api.patch<User>(`/users/${id}`, data).then(res => res.data),
    deleteUser: (id: string) => api.delete(`/users/${id}`).then(res => res.data),
    verifyVeterinarian: (id: string, status: 'verified' | 'rejected' | 'pending') => api.patch(`/users/veterinarians/${id}/verify`, { status }).then(res => res.data),
    getOperationalStats: () => api.get(`/admin/operational-stats`).then(r => r.data),
};

import {
    Device,
    SimCard,
    DcmStats,
    DeviceLog,
    MaintenanceLog,
    DeviceQueryParams,
    SimQueryParams
} from '@/types/dcm';

// ... (existing code preserved)

// DCM (Device & Connectivity Manager) API
export const dcmApi = {
    // Dispositivos
    listDevices: (params?: DeviceQueryParams) => api.get<Device[]>('/admin/dcm/devices', { params }).then(res => res.data),
    createDevice: (data: Partial<Device>) => api.post<Device>('/admin/dcm/devices', data).then(res => res.data),

    // Chips
    listSims: (params?: SimQueryParams) => api.get<SimCard[]>('/admin/dcm/sims', { params }).then(res => res.data),
    createSim: (data: Partial<SimCard>) => api.post<SimCard>('/admin/dcm/sims', data).then(res => res.data),

    // Vínculo & Operações Técnicas
    linkSim: (trackerId: string, simId: string) => api.post(`/admin/dcm/devices/${trackerId}/link-sim`, { sim_id: simId }).then(res => res.data),
    getDeviceLogs: (trackerId: string) => api.get<DeviceLog[]>(`/admin/dcm/devices/${trackerId}/logs`).then(res => res.data),
    swapDevice: (data: { old_tracker_id: string, new_tracker_id: string, reason: string }) => api.post('/admin/dcm/devices/swap', data).then(res => res.data),
    getStats: () => api.get<DcmStats>('/admin/dcm/stats').then(res => res.data),
    bulkImportDevices: (data: { imeis: string | string[], model?: string, supplier?: string }) => api.post<{ message: string }>('/admin/dcm/devices/bulk-import', data).then(res => res.data),
    addMaintenanceLog: (trackerId: string, data: { issue_type: string, description?: string, ticket_id?: string }) => api.post<MaintenanceLog>(`/admin/dcm/devices/${trackerId}/maintenance`, data).then(res => res.data),
    getMaintenanceLogs: (trackerId: string) => api.get<MaintenanceLog[]>(`/admin/dcm/devices/${trackerId}/maintenance`).then(res => res.data),
};

// =============================================================
// Notificações
// =============================================================
export const notificationsApi = {
    list: (params?: { limit?: number; unreadOnly?: boolean }) =>
        api.get('/notifications', { params }).then((r) => r.data),

    getUnreadCount: () =>
        api.get<{ count: number }>('/notifications/unread-count').then((r) => r.data),

    markAsRead: (id: string) =>
        api.patch(`/notifications/${id}/read`).then((r) => r.data),

    markAllAsRead: () =>
        api.patch('/notifications/read-all').then((r) => r.data),
};

// =============================================================
// Health (Carteira de Saúde)
// =============================================================
export const healthApi = {
    getWallet: (petId: string) => api.get(`/pets/${petId}/health`).then(r => r.data),
    addVaccine: (petId: string, data: Record<string, unknown>) => api.post(`/pets/${petId}/vaccines`, data).then(r => r.data),
    addMedication: (petId: string, data: Record<string, unknown>) => api.post(`/pets/${petId}/medications`, data).then(r => r.data),
    stopMedication: (medicationId: string) => api.patch(`/pets/medications/${medicationId}/stop`).then(r => r.data),
    markScheduleTaken: (scheduleId: string) => api.patch(`/pets/schedules/${scheduleId}/taken`).then(r => r.data),
    addNutritionLog: (petId: string, data: Record<string, unknown>) => api.post(`/pets/${petId}/nutrition`, data).then(r => r.data),
};

// =============================================================
// Agenda (Agendamentos e Clínicas Parceiras)
// =============================================================
export const agendaApi = {
    getPartners: (category?: string) => api.get(`/agenda/partners`, { params: { category } }).then(r => r.data),
    getPartnerServices: (partnerId: string) => api.get(`/agenda/partners/${partnerId}/services`).then(r => r.data),
    getAvailability: (partnerId: string, date: string, serviceId: string) => api.get(`/agenda/partners/${partnerId}/availability`, { params: { date, serviceId } }).then(r => r.data),
    createAppointment: (data: Record<string, unknown>) => api.post(`/agenda/appointments`, data).then(r => r.data),
    updateAppointmentStatus: (id: string, status: string) => api.patch(`/agenda/appointments/${id}/status`, { status }).then(r => r.data),
    createCheckout: (data: { appointmentId: string }) => api.post(`/payments/checkout`, data).then(r => r.data),
};

// =============================================================
// Recurring Plans
// =============================================================
export const recurringPlansApi = {
    listPlans: () => api.get(`/recurring-plans`).then(r => r.data),
    createPlan: (data: Record<string, unknown>) => api.post(`/recurring-plans`, data).then(r => r.data),
    updatePlan: (id: string, data: Record<string, unknown>) => api.patch(`/recurring-plans/${id}`, data).then(r => r.data),
    completeExecution: (id: string) => api.post(`/recurring-plans/${id}/complete`).then(r => r.data),
};

export default api;
